import {useEffect, useState} from "react";

import SlatePromptEditor from "@/components/settings/PromptEditor/SlatePromptEditor";
import {Alert, AlertDescription} from "@/components/ui/Alert";
import {Button} from "@/components/ui/Button";
import {Eye, Pencil} from "lucide-react";
import {ActionVariable, VariableTypes} from "@/types/node";
import {PromptVariable} from "@/types/node";
import {
  fillVariablesInJson,
  formatJsonValue,
  isValidJson,
} from "@/utils/jsonHelper";
import JsonContainer from "./JsonContainer";

const variableParams = {
  [VariableTypes.VARIABLES]: {
    label: "Variables",
    icon: "<>",
    open: "<",
    close: ">",
    content: [] as PromptVariable[],
  },
};

const transformVariables = (variables: ActionVariable[][]): string[] => {
  return variables.map(
    variable => variable.find(v => v.key === "key")?.value || "",
  );
};

const JsonEditor = ({
  value,
  onBlur,
  onChange,
  variables,
  disabled,
}: {
  value: string;
  onBlur?: () => void;
  onChange: (value: string) => void;
  variables: ActionVariable[][];
  disabled: boolean;
}) => {
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isViewMode, setIsViewMode] = useState<boolean>(false);
  const [parsedValue, setParsedValue] = useState<object>({});

  const handleSwitchToEditMode = () => {
    setIsViewMode(!isViewMode);
  };

  useEffect(() => {
    setIsValid(isValidJson(value));
  }, [value]);

  useEffect(() => {
    if (isValid && isViewMode) {
      setParsedValue(JSON.parse(fillVariablesInJson(value, variables)));
    }
  }, [isViewMode, isValid]);

  return (
    <>
      <div className="relative">
        <Button
          variant="outline"
          size="icon-sm"
          onClick={handleSwitchToEditMode}
          className="absolute top-2 right-2 z-10"
          disabled={!isValid}
        >
          <Eye className="w-4 h-4" />
        </Button>
        {isViewMode ? (
          <>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={handleSwitchToEditMode}
              className="absolute top-2 right-2 z-10 bg-gray-800 text-label border-gray-600 hover:bg-gray-800 hover:border-gray-500"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <JsonContainer
              data={parsedValue}
              style={{
                minHeight: 112,
              }}
            />
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={handleSwitchToEditMode}
              className="absolute top-2 right-2 z-10"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <SlatePromptEditor
              value={formatJsonValue(value) || ""}
              placeholder="{}"
              onChange={onChange}
              onBlur={onBlur}
              textareaClassName="rounded-lg focus:border-primary focus:ring-0"
              hideLegend={true}
              variableParams={variableParams}
              getVariablesData={(searchParam: string) => {
                const transformedVariables = transformVariables(variables);
                return transformedVariables.filter(variable =>
                  (variable as string).includes(searchParam.toLowerCase()),
                );
              }}
              disabled={disabled}
            />
          </>
        )}
        {!isValid && (
          <Alert
            variant="destructive"
            className="mt-4 cursor-pointer"
            onClick={() => {
              // TODO: fix json value
            }}
          >
            <AlertDescription>Invalid JSON</AlertDescription>
          </Alert>
        )}
      </div>
    </>
  );
};

export default JsonEditor;
